const DOCUMENT_GENERATOR_TEMPLATES_BASE = '/document-generator/';
const NOTIFICATIONS_BASE = '/notification/';
const DOCUMENT_INSTANCE_BASE = '/document-instances/';
const DOCUMENT_INSTANCE_VERSION_BASE = '/document-instance-versions/';
const OPENAI_BASE = '/openai/';
const CHAT_BASE = '/chat/';
const MESSAGE_BASE = '/messages/';
const PROMPT_CATEGORY_BASE = '/prompt-categories/';
const PROMPT_BASE = '/prompts/';
const PAST_MEDICAL_EXPENSE_BASE = '/past-medical-expenses/';
const FUTURE_MEDICAL_EXPENSE_BASE = '/future-medical-expenses/';
const DOCUMENT_PROMPT_PREVIEW_BASE = '/document-prompt-previews/';
const DOCUMENT_GENERATOR_VERSION_BASE = '/document-generator-versions/';
const USER_BASE = '/user/';

export const urls = {
  myDocuments: '/my-documents/',
  documentGeneratorTemplate: {
    base: DOCUMENT_GENERATOR_TEMPLATES_BASE,
    simple: `${DOCUMENT_GENERATOR_TEMPLATES_BASE}simple/`,
    tokenUsage: `${DOCUMENT_GENERATOR_TEMPLATES_BASE}token-usage/`,
    byId: (id: number) => `${DOCUMENT_GENERATOR_TEMPLATES_BASE}${id}/`,
    updateOrder: (id: number) =>
      `${DOCUMENT_GENERATOR_TEMPLATES_BASE}${id}/update-order/`,
    createWithVersion: `${DOCUMENT_GENERATOR_TEMPLATES_BASE}create-with-version/`,
    currentVersion: (templateId: number) =>
      `${DOCUMENT_GENERATOR_TEMPLATES_BASE}${templateId}/current-version/`,
    duplicate: (id: number) =>
      `${DOCUMENT_GENERATOR_TEMPLATES_BASE}${id}/duplicate/`,
  },
  documentGeneratorVersion: {
    byId: (id: number) => `${DOCUMENT_GENERATOR_VERSION_BASE}${id}/`,
    uploadFiles: `${DOCUMENT_GENERATOR_VERSION_BASE}upload-files/`,
    filesById: (id: number, versionId: number) =>
      `${DOCUMENT_GENERATOR_VERSION_BASE}${versionId}/files/${id}/`,
    generate: (id: number) =>
      `${DOCUMENT_GENERATOR_VERSION_BASE}${id}/generate/`,
    getHtmlContent: (id: number) =>
      `${DOCUMENT_GENERATOR_VERSION_BASE}${id}/get-html-content/`,
  },
  documentInstance: {
    base: DOCUMENT_INSTANCE_BASE,
    byId: (id: number) => `${DOCUMENT_INSTANCE_BASE}${id}/`,
    cancelGeneration: (id: number) =>
      `${DOCUMENT_INSTANCE_BASE}${id}/cancel-generation/`,
    exportDocx: (id: number) => `${DOCUMENT_INSTANCE_BASE}${id}/export-docx/`,
  },
  documentInstanceVersion: {
    base: DOCUMENT_INSTANCE_VERSION_BASE,
    byId: (id: number) => `${DOCUMENT_INSTANCE_VERSION_BASE}${id}/`,
    createEmpty: `${DOCUMENT_INSTANCE_VERSION_BASE}create-empty-document-instance/`,
    currentVersion: `${DOCUMENT_INSTANCE_VERSION_BASE}get-current-version/`,
    makeCurrent: (id: number) =>
      `${DOCUMENT_INSTANCE_VERSION_BASE}${id}/make-current-version/`,
    chat: (id: number) => `${DOCUMENT_INSTANCE_VERSION_BASE}${id}/chat/`,
    sendMessage: (id: number) =>
      `${DOCUMENT_INSTANCE_VERSION_BASE}${id}/send-chat-message/`,
    pastMedicalExpenses: (id: number) =>
      `${DOCUMENT_INSTANCE_VERSION_BASE}${id}${PAST_MEDICAL_EXPENSE_BASE}`,
    pastMedicalExpensesById: (id: number, expenseId: number) =>
      `${DOCUMENT_INSTANCE_VERSION_BASE}${id}${PAST_MEDICAL_EXPENSE_BASE}${expenseId}/`,
    futureMedicalExpenses: (id: number) =>
      `${DOCUMENT_INSTANCE_VERSION_BASE}${id}${FUTURE_MEDICAL_EXPENSE_BASE}`,
    futureMedicalExpensesById: (id: number, expenseId: number) =>
      `${DOCUMENT_INSTANCE_VERSION_BASE}${id}${FUTURE_MEDICAL_EXPENSE_BASE}${expenseId}/`,
    extractMedicalExpenses: (id: number) =>
      `${DOCUMENT_INSTANCE_VERSION_BASE}${id}/extract-medical-expenses/`,
    getHtmlContent: (id: number) =>
      `${DOCUMENT_INSTANCE_VERSION_BASE}${id}/get-html-content/`,
    syncHtmlContent: (id: number) =>
      `${DOCUMENT_INSTANCE_VERSION_BASE}${id}/sync-html-content/`,
    getExhibits: (id: number) =>
      `${DOCUMENT_INSTANCE_VERSION_BASE}${id}/get-exhibits/`,
    getOriginalContent: (id: number) =>
      `${DOCUMENT_INSTANCE_VERSION_BASE}${id}/get-original-content/`,
  },
  notifications: {
    base: NOTIFICATIONS_BASE,
  },
  openAI: {
    overallTokenUsage: `${OPENAI_BASE}overall-token-usage/`,
  },
  chat: {
    base: CHAT_BASE,
  },
  messages: {
    base: MESSAGE_BASE,
  },
  promptCategory: {
    base: PROMPT_CATEGORY_BASE,
  },
  prompt: {
    base: PROMPT_BASE,
    updateMultiple: `${PROMPT_BASE}update-multiple/`,
  },
  documentPromptPreview: {
    base: DOCUMENT_PROMPT_PREVIEW_BASE,
    byId: (id: number) => `${DOCUMENT_PROMPT_PREVIEW_BASE}${id}/`,
    saveDraft: `${DOCUMENT_PROMPT_PREVIEW_BASE}save-draft/`,
    generate: (id: number) => `${DOCUMENT_PROMPT_PREVIEW_BASE}${id}/generate/`,
    uploadFiles: `${DOCUMENT_PROMPT_PREVIEW_BASE}upload-files/`,
    filesBase: `${DOCUMENT_PROMPT_PREVIEW_BASE}files/`,
    filesById: (id: number, previewId: number) =>
      `${DOCUMENT_PROMPT_PREVIEW_BASE}${previewId}/files/${id}/`,
  },
  documentTemplate: {
    base: '/document-templates/',
    byId: (id: number) => `/document-templates/${id}/`,
    getHtmlTemplate: (id: number) =>
      `/document-templates/${id}/get-html-template/`,
  },
  incidentState: {
    base: '/incident-states/',
  },
  msGraph: {
    oneDrive: {
      files: '/msgraph/onedrive/',
      createFolder: '/msgraph/onedrive/create-folder/',
      deleteItem: '/msgraph/onedrive/delete-item/',
      renameItem: '/msgraph/onedrive/rename-item/',
      uploadFile: '/msgraph/onedrive/upload-file/',
    },
  },
  referenceDocuments: {
    base: '/reference-documents/',
    tree: '/reference-documents/tree/',
  },
  userTourGuide: {
    base: `${USER_BASE}tour_guide/`,
    secondary: `${USER_BASE}secondary_tour_guide/`,
  },
  company: {
    base: '/companies/',
  },
  companyMembers: {
    base: '/get-company-members/',
  },
  caseSubscription: {
    availablePlans: '/api/case-subscriptions/available-plans/',
    trialEligibility: '/api/case-subscriptions/trial-eligibility/',
    current: '/api/case-subscriptions/current/',
    startTrial: '/api/case-subscriptions/start-trial/',
    subscribe: '/api/case-subscriptions/subscribe/',
    cancel: '/api/case-subscriptions/cancel/',
    usage: '/api/case-subscriptions/usage/',
    // Billing (Option B): replaces legacy /payment/* endpoints
    paymentMethods: '/api/case-subscriptions/payment-methods/',
    addPaymentMethod: '/api/case-subscriptions/payment-methods/add/',
    addPaymentMethodSignup:
      '/api/case-subscriptions/payment-methods/add-signup/',
    setDefaultPaymentMethod:
      '/api/case-subscriptions/payment-methods/set-default/',
    deletePaymentMethod: '/api/case-subscriptions/payment-methods/delete/',
    invoices: '/api/case-subscriptions/invoices/',
    reactivate: '/api/case-subscriptions/reactivate/',
    runManualPayment: '/api/case-subscriptions/run-manual-payment/',
    createTrialSubscription:
      '/api/case-subscriptions/create-trial-subscription/',
    createCheckoutSession: '/api/case-subscriptions/create-checkout-session/',
    upgrade: '/api/case-subscriptions/upgrade/',
  },
  alaCarte: {
    products: '/api/alacarte/products/',
    credits: '/api/alacarte/credits/',
    purchase: '/api/alacarte/purchase/',
    purchaseDirect: '/api/alacarte/purchase-direct/',
    purchases: '/api/alacarte/purchases/',
    receipt: (purchaseId: number) =>
      `/api/alacarte/purchases/${purchaseId}/receipt/`,
    usage: '/api/alacarte/usage/',
    createCheckoutSession: '/api/alacarte/create-checkout-session/',
    createPaymentIntent: '/api/alacarte/create-payment-intent/',
    confirm: '/api/alacarte/confirm/',
  },
  allDocumentTemplates: {
    base: '/all-document-templates/',
  },
  firmProfile: {
    base: '/me/firm-profile/',
    logo: '/me/firm-profile/logo/',
  },
  depositionSummaryTemplate: {
    base: '/deposition-summary-templates/',
    byId: (id: number) => `/deposition-summary-templates/${id}/`,
    uploadFiles: `/deposition-summary-templates/upload-files/`,
    createWithVersion: `/deposition-summary-templates/create-with-version/`,
    generate: (id: number) => `/deposition-summary-templates/${id}/generate/`,
    updateOrder: (id: number) =>
      `/deposition-summary-templates/${id}/update-order/`,
    duplicate: (id: number) => `/deposition-summary-templates/${id}/duplicate/`,
  },
  depositionSummary: {
    byId: (id: number) => `/deposition-summaries/${id}/`,
    filesById: (depositionSummaryId: number, fileId: number) =>
      `/deposition-summaries/${depositionSummaryId}/files/${fileId}/`,
    saveDraft: `/deposition-summaries/save-draft/`,
    uploadFiles: `/deposition-summaries/upload-files/`,
    generate: (id: number) => `/deposition-summaries/${id}/generate/`,
    exportDocx: (id: number) => `/deposition-summaries/${id}/export-docx/`,
    cancelGeneration: (id: number) =>
      `/deposition-summaries/${id}/cancel-generation/`,
    extractDetails: `/deposition-summaries/extract-details/`,
    extractFromCase: `/deposition-summaries/extract-from-case/`,
  },
  depositionSummaryVersion: {
    byId: (id: number) => `/deposition-summary-versions/${id}/`,
    makeCurrent: (id: number) =>
      `/deposition-summary-versions/${id}/make-current-version/`,
    getHtmlContent: (id: number) =>
      `/deposition-summary-versions/${id}/get-html-content/`,
    exportDocx: (id: number) =>
      `/deposition-summary-versions/${id}/export-docx/`,
    // Chat endpoints for Deposition Summary versions
    chat: (id: number) => `/deposition-summary-versions/${id}/chat/`,
    sendMessage: (id: number) =>
      `/deposition-summary-versions/${id}/send-chat-message/`,
  },
  useCaseTemplate: {
    base: '/use-case-templates/',
    byId: (id: number) => `/use-case-templates/${id}/`,
    uploadFiles: `/use-case-templates/upload-files/`,
    createWithVersion: `/use-case-templates/create-with-version/`,
    createWithPrompts: `/use-case-templates/create-with-prompts/`,
    generate: (id: number) => `/use-case-templates/${id}/generate/`,
    updateOrder: (id: number) => `/use-case-templates/${id}/update-order/`,
    duplicate: (id: number) => `/use-case-templates/${id}/duplicate/`,
  },
  useCase: {
    base: '/use-cases/',
    saveDraft: '/use-cases/save-draft/',
    generate: (id: number) => `/use-cases/${id}/generate/`,
    generateFromCase: '/use-cases/generate-from-case/',
    byId: (id: number) => `/use-cases/${id}/`,
    uploadFiles: '/use-cases/upload-files/',
    exportDocx: (id: number) => `/use-cases/${id}/export-docx/`,
    filesById: (useCaseId: number, fileId: number) =>
      `/use-cases/${useCaseId}/files/${fileId}/`,
    cancelGeneration: (id: number) => `/use-cases/${id}/cancel-generation/`,
    linkCase: (id: number) => `/use-cases/${id}/link-case/`,
    generationStatus: (id: number) => `/use-cases/${id}/generation-status/`,
    progress: (id: number) => `/use-cases/${id}/progress/`,
  },
  // Document intake (ZLAYT-1623): a case's documents, search and pages.
  caseDocuments: {
    list: (caseId: number) => `/cases/${caseId}/documents/`,
    byId: (caseId: number, documentId: number) =>
      `/cases/${caseId}/documents/${documentId}/`,
    page: (caseId: number, documentId: number) =>
      `/cases/${caseId}/documents/${documentId}/page/`,
    search: (caseId: number) => `/cases/${caseId}/search/`,
    types: '/document-types/',
  },
  useCaseVersion: {
    byId: (id: number) => `/use-case-versions/${id}/`,
    getHtmlContent: (id: number) =>
      `/use-case-versions/${id}/get-html-content/`,
    syncHtmlContent: (id: number) =>
      `/use-case-versions/${id}/sync-html-content/`,
    makeCurrent: (id: number) =>
      `/use-case-versions/${id}/make-current-version/`,
    // Chat endpoints for Use Case versions
    chat: (id: number) => `/use-case-versions/${id}/chat/`,
    sendMessage: (id: number) => `/use-case-versions/${id}/send-chat-message/`,
    exportDocx: (id: number) => `/use-case-versions/${id}/export-docx/`,
    getExhibits: (id: number) => `/use-case-versions/${id}/get-exhibits/`,
    getOriginalContent: (id: number) =>
      `/use-case-versions/${id}/get-original-content/`,
  },
  useCaseGroup: {
    base: '/use-case-groups/',
    byId: (id: number) => `/use-case-groups/${id}/`,
    updateOrder: (id: number) => `/use-case-groups/${id}/update-order/`,
  },
  agents: {
    base: '/agents/',
    byId: (id: number) => `/agents/${id}/`,
    publish: (id: number) => `/agents/${id}/publish/`,
    duplicate: (id: number) => `/agents/${id}/duplicate/`,
    files: (id: number) => `/agents/${id}/files/`,
    fileById: (agentId: number, fileId: string) =>
      `/agents/${agentId}/files/${fileId}/`,
    tools: (id: number) => `/agents/${id}/tools/`,
    conversationStarters: (id: number) =>
      `/agents/${id}/conversation-starters/`,
    createPreview: (id: number) => `/agents/${id}/create-preview-session/`,
    createLive: (id: number) => `/agents/${id}/create-live-session/`,
  },
  agentChat: {
    history: '/agent-chat/history/',
    sessions: {
      base: '/agent-chat/sessions/',
      byId: (sessionId: number) => `/agent-chat/sessions/${sessionId}/`,
      messages: (sessionId: number) =>
        `/agent-chat/sessions/${sessionId}/messages/`,
    },
  },
  tools: {
    base: '/agent-tools/',
  },
  conversationStarters: {
    base: '/agent-conversation-starters/',
  },
  transcribeAudioToText: '/transcribe-audio/',
  transcribeDoc: '/transcribe-doc/',
  // Public, no sign-in: which third-party widgets the backend has switched on.
  publicWidgets: '/api/public/widgets/',
};
